/**
 * QA Panel — Console / Errors controller.
 *
 * Captures runtime errors and warnings so QA can inspect what blew up during
 * a session without needing devtools open. Hooks the four browser-level
 * channels that surface failures:
 *
 *   - `console.error` / `console.warn`     — explicit dev logs
 *   - `window.onerror`                     — uncaught throws
 *   - `window.error` (capture)             — resource load failures (img/script/css)
 *   - `window.unhandledrejection`          — failed fetches and async throws
 *
 * Each hook is chained: the existing handler (if any) is preserved and
 * called after the capture so we don't trample Sentry / framework wiring.
 * `destroy()` restores everything.
 *
 * Entries are deduped on `level|message|firstStackFrame` and stored in a
 * 200-cap ring buffer. The list is wiped automatically on lobby navigation
 * (selectedSection / viewMode change) — the user confirmed this is the
 * desired behaviour. Errors that fire mid-render typically come in bursts,
 * so `onChange` is debounced (~200ms) to avoid swamping the popup.
 */

import { useAppStore } from '../../../store/app/appStore';

/* ── Public state shape sent to the popup ───────────────────────── */

export type ConsoleLevel = 'error' | 'warn';

export type ConsoleCategory =
    | 'game-client'
    | 'asset'
    | 'api'
    | 'lobby-ui'
    | 'lobby-logic'
    | 'unknown';

export interface ConsoleEntrySource {
    /** Where the entry was captured. */
    type: 'console' | 'window-error' | 'resource' | 'unhandledrejection';
    /**
     * Free-form detail string — for resource errors this is the failing
     * URL + tag name, for window.onerror it's `file:line:col`, etc.
     */
    detail: string;
}

export interface ConsoleEntry {
    id: string;
    level: ConsoleLevel;
    message: string;
    stack: string | null;
    category: ConsoleCategory;
    /** Number of times an entry with this hash has been observed. */
    count: number;
    firstSeenAt: number;
    lastSeenAt: number;
    source: ConsoleEntrySource;
}

export interface ConsoleState {
    entries: ConsoleEntry[];
    /** Total number of raw events captured (pre-dedupe). */
    totalCaptured: number;
}

/* ── Constants ──────────────────────────────────────────────────── */

/** Max distinct entries kept in memory. Oldest-first eviction. */
const MAX_ENTRIES = 200;
/** Debounce window for onChange — error bursts can fire dozens per tick. */
const ONCHANGE_DEBOUNCE_MS = 200;
/** Soft cap on stored message length so a runaway payload can't bloat memory. */
const MAX_MESSAGE_CHARS = 4000;
/** Soft cap on stored stack length. */
const MAX_STACK_CHARS = 6000;

/* ── Categorization heuristics ──────────────────────────────────── */

/**
 * Order matters — the most specific buckets are checked first so a stack
 * that mentions both `core/api/` and `features/` lands in `api` (the more
 * specific signal).
 *
 * The asset bucket also checks the live `assetsHost` from appStore (read
 * once per categorize call, guarded for null) so on environments where the
 * CDN host differs from the lobby origin we still bucket image/video 404s
 * correctly.
 */
function categorize(
    message: string,
    stack: string | null,
    source: ConsoleEntrySource,
): ConsoleCategory {
    // Resource load failures are always assets — the source already tells us.
    if (source.type === 'resource') return 'asset';

    const haystack = `${message}\n${stack ?? ''}`;

    if (
        haystack.includes('postMessage') ||
        haystack.includes('ExternalCommunication') ||
        haystack.includes('valtio')
    ) {
        return 'game-client';
    }

    let assetsHost = '';
    try {
        assetsHost = useAppStore.getState().settings.assetsHost ?? '';
    } catch {
        assetsHost = '';
    }
    if (assetsHost && haystack.includes(assetsHost)) {
        return 'asset';
    }

    if (
        haystack.includes('core/api/') ||
        /Failed to fetch|NetworkError|HTTP \d{3}/.test(haystack)
    ) {
        return 'api';
    }

    if (
        haystack.includes('The above error occurred in the <') ||
        haystack.includes('node_modules/react-dom') ||
        haystack.includes('react-dom.')
    ) {
        return 'lobby-ui';
    }

    if (haystack.includes('features/') || haystack.includes('/store/')) {
        return 'lobby-logic';
    }

    return 'unknown';
}

/* ── Helpers ────────────────────────────────────────────────────── */

function safeToString(v: unknown): string {
    if (typeof v === 'string') return v;
    if (v instanceof Error) return v.message || v.name || 'Error';
    if (v === null) return 'null';
    if (v === undefined) return 'undefined';
    try {
        return JSON.stringify(v);
    } catch {
        try {
            return String(v);
        } catch {
            return '[unserializable]';
        }
    }
}

function argsToMessage(args: unknown[]): string {
    return args.map(safeToString).join(' ').slice(0, MAX_MESSAGE_CHARS);
}

function firstStackFrame(stack: string | null): string {
    if (!stack) return '';
    const lines = stack.split('\n');
    // Stack typically starts with "Error: …" — first real frame is the next line.
    for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('at ')) return trimmed;
    }
    return lines[0]?.trim() ?? '';
}

function makeHash(level: ConsoleLevel, message: string, stack: string | null): string {
    return `${level}|${message}|${firstStackFrame(stack)}`;
}

function makeId(): string {
    // No crypto dep — Date.now + random suffix is plenty for popup-row keying.
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function describeResourceTarget(target: EventTarget | null): { detail: string; message: string } {
    if (!target) return { detail: '(unknown target)', message: 'Resource failed to load' };
    if (target instanceof HTMLImageElement) {
        return {
            detail: `<img> ${target.currentSrc || target.src || '(no src)'}`,
            message: `Image failed to load: ${target.currentSrc || target.src || '(no src)'}`,
        };
    }
    if (target instanceof HTMLVideoElement) {
        return {
            detail: `<video> ${target.currentSrc || target.src || '(no src)'}`,
            message: `Video failed to load: ${target.currentSrc || target.src || '(no src)'}`,
        };
    }
    if (target instanceof HTMLScriptElement) {
        return {
            detail: `<script> ${target.src || '(no src)'}`,
            message: `Script failed to load: ${target.src || '(no src)'}`,
        };
    }
    if (target instanceof HTMLLinkElement) {
        return {
            detail: `<link> ${target.href || '(no href)'}`,
            message: `Stylesheet failed to load: ${target.href || '(no href)'}`,
        };
    }
    return { detail: '(non-resource target)', message: 'Resource failed to load' };
}

/* ── Controller ─────────────────────────────────────────────────── */

type ConsoleMethod = (...args: unknown[]) => void;

export class ConsoleController {
    private onChange: (() => void) | null = null;
    private entries: ConsoleEntry[] = [];
    /** Hash → index in `entries`. */
    private byHash = new Map<string, number>();
    private totalCaptured = 0;

    private originalError: ConsoleMethod | null = null;
    private originalWarn: ConsoleMethod | null = null;
    private originalOnError: OnErrorEventHandler | null = null;
    private windowErrorHandler: ((event: ErrorEvent) => void) | null = null;
    private unhandledRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
    private storeUnsubscribe: (() => void) | null = null;
    private debounceHandle: number | null = null;

    init(onChange: () => void): void {
        this.onChange = onChange;

        // Chain console.error — capture, then forward to the original (which
        // may itself be a chain set up by Sentry or another framework).
        this.originalError = console.error.bind(console);
        console.error = (...args: unknown[]) => {
            this.captureFromConsole('error', args);
            this.originalError?.(...args);
        };

        this.originalWarn = console.warn.bind(console);
        console.warn = (...args: unknown[]) => {
            this.captureFromConsole('warn', args);
            this.originalWarn?.(...args);
        };

        // Chain window.onerror. Note: this catches uncaught throws but not
        // resource-load failures — those need the capture-phase listener below.
        this.originalOnError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
            this.captureFromWindowError(message, source, lineno, colno, error);
            // Call the previous handler if any, preserving its semantics.
            if (typeof this.originalOnError === 'function') {
                return this.originalOnError.call(window, message, source, lineno, colno, error);
            }
            return false;
        };

        // Resource load failures (img/video/script/link 404s) only surface via
        // capture-phase listeners on window — they don't bubble.
        this.windowErrorHandler = (event: ErrorEvent) => {
            this.captureFromWindowEvent(event);
        };
        window.addEventListener('error', this.windowErrorHandler, true);

        // Failed fetches and unhandled promise rejections.
        this.unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
            this.captureFromRejection(event);
        };
        window.addEventListener('unhandledrejection', this.unhandledRejectionHandler);

        // Clear on lobby navigation — same selector pattern as BackofficeController.
        this.storeUnsubscribe = useAppStore.subscribe((state, prev) => {
            if (
                state.selectedSection !== prev.selectedSection ||
                state.viewMode !== prev.viewMode
            ) {
                this.clear();
            }
        });
    }

    destroy(): void {
        // Restore console methods using the originals captured at init.
        if (this.originalError) {
            console.error = this.originalError;
            this.originalError = null;
        }
        if (this.originalWarn) {
            console.warn = this.originalWarn;
            this.originalWarn = null;
        }
        window.onerror = this.originalOnError;
        this.originalOnError = null;

        if (this.windowErrorHandler) {
            window.removeEventListener('error', this.windowErrorHandler, true);
            this.windowErrorHandler = null;
        }
        if (this.unhandledRejectionHandler) {
            window.removeEventListener('unhandledrejection', this.unhandledRejectionHandler);
            this.unhandledRejectionHandler = null;
        }
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;

        if (this.debounceHandle !== null) {
            window.clearTimeout(this.debounceHandle);
            this.debounceHandle = null;
        }
        this.onChange = null;
    }

    getState(): ConsoleState {
        // Newest first for the popup — easier to scan.
        return {
            entries: this.entries.slice().reverse(),
            totalCaptured: this.totalCaptured,
        };
    }

    clear(): void {
        if (this.entries.length === 0 && this.totalCaptured === 0) return;
        this.entries = [];
        this.byHash.clear();
        this.totalCaptured = 0;
        this.scheduleOnChange();
    }

    /* ── Capture entry points ────────────────────────────────────── */

    private captureFromConsole(level: ConsoleLevel, args: unknown[]): void {
        const message = argsToMessage(args);
        // If one of the args is an Error, prefer its stack.
        let stack: string | null = null;
        for (const a of args) {
            if (a instanceof Error && a.stack) {
                stack = a.stack.slice(0, MAX_STACK_CHARS);
                break;
            }
        }
        this.record({
            level,
            message,
            stack,
            source: { type: 'console', detail: `console.${level}` },
        });
    }

    private captureFromWindowError(
        message: Event | string,
        source: string | undefined,
        lineno: number | undefined,
        colno: number | undefined,
        error: Error | undefined,
    ): void {
        const msgStr = typeof message === 'string' ? message : safeToString(message);
        const stack = error?.stack ? error.stack.slice(0, MAX_STACK_CHARS) : null;
        const detail = source
            ? `${source}:${lineno ?? '?'}:${colno ?? '?'}`
            : 'window.onerror';
        this.record({
            level: 'error',
            message: msgStr.slice(0, MAX_MESSAGE_CHARS),
            stack,
            source: { type: 'window-error', detail },
        });
    }

    private captureFromWindowEvent(event: ErrorEvent): void {
        // Resource-load failures arrive with no `error` field — the target
        // element is the only signal. Distinguish from regular JS errors
        // (which the onerror chain already covered) by checking the target.
        const target = event.target;
        const isResource =
            target instanceof HTMLImageElement ||
            target instanceof HTMLVideoElement ||
            target instanceof HTMLScriptElement ||
            target instanceof HTMLLinkElement;
        if (!isResource) return; // JS errors already captured via onerror chain.

        const { detail, message } = describeResourceTarget(target);
        this.record({
            level: 'error',
            message,
            stack: null,
            source: { type: 'resource', detail },
        });
    }

    private captureFromRejection(event: PromiseRejectionEvent): void {
        const reason: unknown = event.reason;
        let message = '';
        let stack: string | null = null;
        if (reason instanceof Error) {
            message = reason.message || reason.name || 'Unhandled rejection';
            stack = reason.stack ? reason.stack.slice(0, MAX_STACK_CHARS) : null;
        } else {
            message = `Unhandled rejection: ${safeToString(reason)}`;
        }
        this.record({
            level: 'error',
            message: message.slice(0, MAX_MESSAGE_CHARS),
            stack,
            source: { type: 'unhandledrejection', detail: 'window.unhandledrejection' },
        });
    }

    /* ── Internal: dedupe + ring buffer ──────────────────────────── */

    private record(input: {
        level: ConsoleLevel;
        message: string;
        stack: string | null;
        source: ConsoleEntrySource;
    }): void {
        this.totalCaptured += 1;
        const hash = makeHash(input.level, input.message, input.stack);
        const now = Date.now();

        const existingIndex = this.byHash.get(hash);
        if (existingIndex !== undefined) {
            const existing = this.entries[existingIndex];
            if (existing) {
                existing.count += 1;
                existing.lastSeenAt = now;
                this.scheduleOnChange();
                return;
            }
        }

        const entry: ConsoleEntry = {
            id: makeId(),
            level: input.level,
            message: input.message,
            stack: input.stack,
            category: categorize(input.message, input.stack, input.source),
            count: 1,
            firstSeenAt: now,
            lastSeenAt: now,
            source: input.source,
        };
        this.entries.push(entry);
        this.byHash.set(hash, this.entries.length - 1);

        // Evict oldest entries when over cap. Rebuilding the hash index is
        // cheap at 200 items and avoids drift.
        if (this.entries.length > MAX_ENTRIES) {
            this.entries.splice(0, this.entries.length - MAX_ENTRIES);
            this.byHash.clear();
            this.entries.forEach((e, i) => {
                this.byHash.set(makeHash(e.level, e.message, e.stack), i);
            });
        }

        this.scheduleOnChange();
    }

    private scheduleOnChange(): void {
        if (this.debounceHandle !== null) return;
        this.debounceHandle = window.setTimeout(() => {
            this.debounceHandle = null;
            this.onChange?.();
        }, ONCHANGE_DEBOUNCE_MS);
    }
}
